import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import YearPicker from '../../../Components/YearPicker/YearPicker';
import MonthPicker from '../../../Components/MonthPicker/MonthPicker';
import { useCallback, useEffect, useState } from 'react';
import { useResellerReport } from '../../../Hooks/useResellerReport';
import { getFinancialMonth } from './utils';
import { Button } from 'primereact/button';
import UseAxios from '../../../Api/http/http';
import { exportResellerInvoice, fetchResellerInvoice } from '../../../Api/request/resellerReport';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { useDebounce } from 'primereact/hooks';
import { useAuth } from '../../../Contexts/AuthContext';
import downloadIcon from '../../../Assets/images/svg/dar_mis_reseller_download.svg';
import exportToExcel from '../../../Utils/exportToExcel';
import { MIS_RESELLER_REPORT } from '../../../Constants/strings';
import ResellerImport from '../ResellerComponents/ResellerImport';
export const formatYearFull = (dateObj) => {
  const startYear = dateObj.getFullYear();
  const endYear = startYear + 1;
  return `${startYear} - ${endYear.toString()}`;
};

const ResellerReportTableToolbar = () => {
  const {
    resellerTableData,
    setResellerTableData,
    financialYear,
    financialMonth,
    initialLoad,
    setInitialLoad,
    setSearchQuery,
  } = useResellerReport();
  const { snackbarOpen } = useSnackbar();
  const { userProfile } = useAuth();
  const { access_token, email, employeeId, user_name } = userProfile;
  const [inputValue, debouncedValue, setInputValue] = useDebounce('', 400);
  const handleResellerSubmit = async (event) => {
    event.preventDefault();
    if (financialYear === '' || financialMonth === '') return;
    try {
      if (initialLoad) setInitialLoad(false);
      const requestBody = {
        financialYear: financialYear
          .split(' ')
          .map((item, index) => (index === 2 ? item.substring(2) : item))
          .join(''),
        month: financialMonth,
        access_token,
        email,
        employeeId,
        user_name,
      };
      const response = await UseAxios(fetchResellerInvoice(requestBody));
      setResellerTableData(() => {
        if (typeof response.data === 'string'){
          return JSON.parse(response.data);
        } else {
          return response.data;
        }
      });
    } catch (error) {
      snackbarOpen('Some unknown error occurred', 'error');
    }
  };

  const updateGlobalSearch = useCallback(() => {
    setSearchQuery(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    updateGlobalSearch();
  }, [updateGlobalSearch]);

  const handleResellerExport = async () => {
    try {
      const requestBody = {
        financialYear: financialYear
          .split(' ')
          .map((item, index) => (index === 2 ? item.substring(2) : item))
          .join(''),
        month: financialMonth,
        access_token,
        email,
        employeeId,
        user_name,
      };
      const response = await UseAxios(exportResellerInvoice(requestBody));
      const requiredData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (!requiredData.length){
        throw new Error('Some error');
      }
      return exportToExcel({
        data: requiredData,
        workBookTitle: MIS_RESELLER_REPORT.TITLE,
        workSheetTitle: MIS_RESELLER_REPORT.TITLE,
      });
    } catch (error) {
      snackbarOpen('Export failed', 'error');
    }
  };

  return (
    <div className="reseller-report-table-tool-bar">
      <form
        onSubmit={handleResellerSubmit}
        className="reseller-report-table-tool-bar-first-compt"
      >
        <Box className="reseller-report-table-search-bar">
          <span className="reseller-report-table-search-icon">
            <i className="pi pi-search "></i>
          </span>
          <TextField
            id="reseller-report-table-search-field"
            className="reseller-report-table-search-input"
            label="Search..."
            variant="standard"
            size="small"
            value={inputValue}
            onChange={(ev) => setInputValue(ev.currentTarget.value)}
          />
        </Box>
        <ResellerReportYearPicker />
        <ResellerReportMonthPicker />
        <Button
          label="Submit"
          icon="pi pi-search"
          className="reseller-report-toolbar-search-button"
          disabled={financialYear === '' || financialMonth === ''}
          type="submit"
        />
      </form>
      <div className="reseller-report-table-tool-bar-second-compt">
        <Tooltip title={'Export'}>
          <IconButton
            id="reseller-report-export-to-excel-btn"
            className="reseller-report-export-to-excel"
            size='small'
            disabled = {resellerTableData.length === 0}
            onClick={handleResellerExport}
          >
            <img src={downloadIcon} alt={'Download button'} />
          </IconButton>
        </Tooltip>
        <ResellerImport/>
      </div>
    </div>
  );
};

const ResellerReportYearPicker = () => {
  const { setFinancialYear } = useResellerReport();
  const [currentYear, setCurrentYear] = useState('');
  const handleDateChange = (date) => {
    setCurrentYear(date.value);
    setFinancialYear(formatYearFull(date.value));
  };
  const [visible, setVisible] = useState(false);
  const handleVisibleChange = () => setVisible((prev) => !prev);
  return (
    <YearPicker
      inputClassName={'reseller-report-year-picker-input'}
      value={currentYear}
      onChange={handleDateChange}
      formatYear={formatYearFull}
      visible={visible}
      onVisibleChange={handleVisibleChange}
      containerClassName={'reseller-report-year-picker'}
    />
  );
};

const ResellerReportMonthPicker = () => {
  const { financialMonth, financialYear, setFinancialMonth } = useResellerReport();
  const [monthValue, setMonthValue] = useState(() => {
    if (financialYear === '') return 'Select';
    getFinancialMonth(financialMonth, financialYear);
  });

  const updateMonth = useCallback(() => {
    setMonthValue(() => {
      if (financialYear === '' || financialMonth === '') return 'Select';
      return getFinancialMonth(financialMonth, financialYear);
    });
  }, [financialYear]);

  useEffect(() => {
    updateMonth();
  }, [updateMonth]);

  const [visible, setVisible] = useState(false);
  const formatMonth = useCallback((months) => {
    return months.map((month) => {
      return getFinancialMonth(month, financialYear === '' ? '2024 - 2025' : financialYear);
    });
  }, [financialYear]);
  const handleChange = (month) => {
    setMonthValue(month);
    setFinancialMonth(() => month.split("'")[0]);
  };
  return (
    <MonthPicker
      inputClassName={'reseller-report-year-picker-input'}
      value={monthValue}
      onChange={handleChange}
      visible={visible}
      onVisibleChange={setVisible}
      containerClassName={'reseller-report-year-picker'}
      formatMonths={formatMonth}
      disabled={financialYear === ''}
    />
  );
};

export default ResellerReportTableToolbar;
